import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileCTA from '@/components/MobileCTA';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { Truck, CheckCircle, TreePine, Star, Shield, Droplets, Leaf, ArrowRight, Phone, Thermometer, Palette } from 'lucide-react';
import SEO from '@/components/SEO';
import ExpandedFAQSection from '@/components/ExpandedFAQSection';
import RelatedMaterialsLinks from '@/components/RelatedMaterialsLinks';
import { mulchFaqs } from '@/data/productFaqs';
import mulchVarietiesImage from '@/assets/mulch-varieties.jpg';
import hardwoodMulch from '@/assets/hardwood-mulch.webp';
import blackMulch from '@/assets/black-mulch.webp';

interface Product {
  id: string;
  slug_en: string;
  slug_es: string;
  name_en: string;
  name_es: string;
  description_en: string;
  description_es: string;
  features_en: string[];
  features_es: string[];
  is_featured: boolean;
  price_book: {
    list_price: number;
    sale_price: number | null;
    unit_label_en: string;
    unit_label_es: string;
  }[];
}

// Static mulch varieties data
const mulchVarieties = [
  {
    id: 'black-mulch',
    name: 'Black Hardwood Mulch',
    nameEs: 'Mulch Negro de Madera Dura',
    price: 28,
    image: blackMulch,
    description: 'Premium black-colored hardwood mulch for elegant garden aesthetics and moisture retention',
    descriptionEs: 'Mulch premium de madera dura color negro para estética elegante del jardín y retención de humedad',
    features: ['Rich Black Color', 'Moisture Retention', 'Weed Suppression', 'Long Lasting'],
    featuresEs: ['Color Negro Rico', 'Retención de Humedad', 'Supresión de Malezas', 'Duradero'],
    coverage: 'Standard coverage per cubic yard',
    lifespan: '12-18 months',
    bestFor: ['Flower beds', 'Tree rings', 'Modern landscapes', 'Contrast gardens'],
    bestForEs: ['Lechos de flores', 'Anillos de árboles', 'Paisajes modernos', 'Jardines de contraste']
  },
  {
    id: 'brown-mulch',
    name: 'Brown Hardwood Mulch',
    nameEs: 'Mulch Marrón de Madera Dura',
    price: 26,
    image: hardwoodMulch,
    description: 'Natural brown hardwood mulch that complements any landscape design beautifully',
    descriptionEs: 'Mulch natural marrón de madera dura que complementa hermosamente cualquier diseño de paisaje',
    features: ['Natural Brown Color', 'Organic Material', 'Soil Improvement', 'Cost Effective'],
    featuresEs: ['Color Marrón Natural', 'Material Orgánico', 'Mejora del Suelo', 'Económico'],
    coverage: 'Standard coverage per cubic yard',
    lifespan: '12-15 months',
    bestFor: ['Traditional gardens', 'Pathways', 'Large areas', 'Commercial properties'],
    bestForEs: ['Jardines tradicionales', 'Senderos', 'Áreas grandes', 'Propiedades comerciales']
  },
];

const MulchPage = () => {
  const { language, t } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMulchProducts = async () => {
      try {
        const { data: category } = await supabase
          .from('categories')
          .select('id')
          .eq(`slug_${language}`, 'mulch')
          .single();

        if (category) {
          const { data: mulchProducts } = await supabase
            .from('products')
            .select(`
              *,
              price_book (
                list_price,
                sale_price,
                unit_label_en,
                unit_label_es
              )
            `)
            .eq('category_id', category.id)
            .eq('is_active', true)
            .order('sort_order');

          setProducts(mulchProducts || []);
        }
      } catch (error) {
        console.error('Error fetching mulch products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMulchProducts();
  }, [language]);

  const getProductName = (product: Product) => 
    language === 'en' ? product.name_en : product.name_es;
  
  const getProductDescription = (product: Product) => 
    language === 'en' ? product.description_en : product.description_es;
  
  const getProductFeatures = (product: Product) => 
    language === 'en' ? product.features_en : product.features_es;

  const getUnitLabel = (product: Product) => 
    language === 'en' ? product.price_book[0]?.unit_label_en : product.price_book[0]?.unit_label_es;

  const seoTitle = language === 'en' 
    ? 'Premium Organic Mulch Delivery Texas | Black, Brown & Red'
    : 'Entrega de Mulch Orgánico Premium Texas | Negro, Marrón y Rojo';

  const seoDescription = language === 'en'
    ? 'Quality organic mulch delivery in Montgomery County TX. Black, brown, red & hardwood mulch. Moisture retention & weed control. Professional delivery.'
    : 'Entrega de mulch orgánico de calidad en el Condado Montgomery TX. Mulch negro, marrón, rojo y de madera dura. Retención de humedad y control de malezas.';
  
  const seoKeywords = language === 'en'
    ? 'mulch delivery texas, organic mulch montgomery county, black mulch conroe, brown mulch woodlands, red mulch cypress, hardwood mulch texas'
    : 'entrega mulch texas, mulch orgánico montgomery county, mulch negro conroe, mulch marrón woodlands, mulch rojo cypress, mulch madera dura texas';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": seoTitle,
    "description": seoDescription,
    "category": "Landscaping Supplies",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "provider": {
      "@type": "LocalBusiness",
      "name": "The Grass Depot LLC",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "TX",
        "addressLocality": "Montgomery County"
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={language === 'en' ? '/en/mulch' : '/es/mulch'}
        structuredData={structuredData}
        language={language}
        breadcrumbs={[
          { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` },
          { name: language === 'en' ? 'Products' : 'Productos', url: language === 'en' ? '/en/products' : '/es/productos' },
          { name: 'Mulch', url: `/${language}/mulch` },
        ]}
      />
      <Header />
      <MobileCTA />
      
      <main>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-hero text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent animate-pulse-glow"></div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="animate-fade-in">
            <div className="flex items-center justify-center mb-6">
              <TreePine className="h-12 w-12 text-primary-foreground mr-4 animate-float" />
              <h1 className="text-4xl md:text-6xl font-bold text-primary-foreground">
                {language === 'en' ? 'Premium Organic Mulch' : 'Mulch Orgánico Premium'}
              </h1>
            </div>
            <p className="text-xl md:text-2xl text-primary-foreground/90 mb-8 max-w-4xl mx-auto leading-relaxed">
              {language === 'en' 
                ? 'High-quality organic mulch for moisture retention, weed control, and beautiful garden beds throughout Montgomery County, Texas.'
                : 'Mulch orgánico de alta calidad para retención de humedad, control de malezas y hermosos lechos de jardín en todo el Condado Montgomery, Texas.'
              }
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <Leaf className="h-5 w-5 mr-2" />
                {language === 'en' ? '🌿 100% Organic Materials' : '🌿 100% Materiales Orgánicos'}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <Shield className="h-5 w-5 mr-2" />
                {language === 'en' ? '🛡️ Natural Weed Control' : '🛡️ Control Natural de Malezas'}
              </Badge>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/${language}/quote`}>
                <Button variant="hero" size="lg" className="hover-scale">
                  {language === 'en' ? 'Get Quote Now' : 'Obtener Cotización Ahora'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:9363200763">
                <Button variant="outline" size="lg" className="bg-white/95 border-white text-primary hover:bg-white hover:text-primary">
                  <Phone className="mr-2 h-5 w-5" />
                  {language === 'en' ? 'Call Expert' : 'Llamar Experto'}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mulch Selection Guide */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Choose Your Perfect Mulch' : 'Elija su Mulch Perfecto'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {language === 'en'
                ? 'Each mulch type offers unique benefits for garden health, aesthetics, and maintenance. Find the perfect match for your landscaping needs.'
                : 'Cada tipo de mulch ofrece beneficios únicos para la salud del jardín, estética y mantenimiento. Encuentre la combinación perfecta para sus necesidades de paisajismo.'
              }
            </p>
          </div>

          <Tabs defaultValue="grid" className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-12">
              <TabsTrigger value="grid">
                {language === 'en' ? 'View All' : 'Ver Todos'}
              </TabsTrigger>
              <TabsTrigger value="benefits">
                {language === 'en' ? 'Benefits' : 'Beneficios'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="grid">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {mulchVarieties.map((mulch, index) => (
                  <Card 
                    key={mulch.id} 
                    className="group hover:shadow-glow transition-all duration-500 border-2 hover:border-primary overflow-hidden animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={mulch.image} 
                        alt={language === 'en' ? mulch.name : mulch.nameEs}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute top-4 right-4 bg-white rounded-lg px-3 py-1 shadow-lg">
                        <span className="font-bold text-primary">
                          ${mulch.price}/yd³
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {language === 'en' ? mulch.name : mulch.nameEs}
                        </h3>
                      </div>
                    </div>
                    
                    <CardContent className="p-6">
                      <p className="text-muted-foreground mb-4 text-sm">
                        {language === 'en' ? mulch.description : mulch.descriptionEs}
                      </p>
                      
                      {/* Features */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {(language === 'en' ? mulch.features : mulch.featuresEs).slice(0, 4).map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-primary flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Specs */}
                      <div className="space-y-2 mb-6 text-xs bg-muted/30 p-3 rounded-lg">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {language === 'en' ? 'Coverage:' : 'Cobertura:'}
                          </span>
                          <span className="font-medium text-right">{mulch.coverage}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {language === 'en' ? 'Lifespan:' : 'Duración:'}
                          </span>
                          <span className="font-medium">{mulch.lifespan}</span>
                        </div>
                      </div>

                      {/* Best For */}
                      <div className="mb-4">
                        <h4 className="font-semibold text-foreground mb-2 text-sm">
                          {language === 'en' ? 'Best For:' : 'Mejor Para:'}
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {(language === 'en' ? mulch.bestFor : mulch.bestForEs).slice(0, 3).map((use, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {use}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Link to={`/${language}/quote`} className="flex-1">
                          <Button variant="hero" className="w-full group-hover:scale-105 transition-transform">
                            {language === 'en' ? 'Add to Quote' : 'Agregar a Cotización'}
                          </Button>
                        </Link>
                        <Button variant="outline" size="icon" className="hover-scale">
                          <TreePine className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="benefits">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <Card className="p-6 text-center hover:shadow-glow transition-all duration-300">
                  <div className="mb-4">
                    <Droplets className="h-12 w-12 text-primary mx-auto" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {language === 'en' ? 'Moisture Retention' : 'Retención de Humedad'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'en'
                      ? 'Reduces watering needs by up to 40% and maintains consistent soil moisture levels.'
                      : 'Reduce las necesidades de riego hasta en un 40% y mantiene niveles consistentes de humedad del suelo.'
                    }
                  </p>
                </Card>

                <Card className="p-6 text-center hover:shadow-glow transition-all duration-300">
                  <div className="mb-4">
                    <Shield className="h-12 w-12 text-primary mx-auto" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {language === 'en' ? 'Weed Suppression' : 'Supresión de Malezas'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'en'
                      ? 'Blocks sunlight to prevent weed germination and reduces maintenance time significantly.'
                      : 'Bloquea la luz solar para prevenir la germinación de malezas y reduce significativamente el tiempo de mantenimiento.'
                    }
                  </p>
                </Card>

                <Card className="p-6 text-center hover:shadow-glow transition-all duration-300">
                  <div className="mb-4">
                    <Thermometer className="h-12 w-12 text-primary mx-auto" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {language === 'en' ? 'Temperature Control' : 'Control de Temperatura'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'en'
                      ? 'Insulates soil to maintain optimal root temperatures in both summer and winter.'
                      : 'Aísla el suelo para mantener temperaturas óptimas de las raíces tanto en verano como en invierno.'
                    }
                  </p>
                </Card>

                <Card className="p-6 text-center hover:shadow-glow transition-all duration-300">
                  <div className="mb-4">
                    <Leaf className="h-12 w-12 text-primary mx-auto" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {language === 'en' ? 'Soil Enhancement' : 'Mejora del Suelo'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'en'
                      ? 'Organic mulches decompose slowly, adding nutrients and improving soil structure over time.'
                      : 'Los mulches orgánicos se descomponen lentamente, agregando nutrientes y mejorando la estructura del suelo con el tiempo.'
                    }
                  </p>
                </Card>

                <Card className="p-6 text-center hover:shadow-glow transition-all duration-300">
                  <div className="mb-4">
                    <Palette className="h-12 w-12 text-primary mx-auto" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {language === 'en' ? 'Aesthetic Appeal' : 'Atractivo Estético'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'en'
                      ? 'Creates clean, finished look while providing consistent color throughout the growing season.'
                      : 'Crea una apariencia limpia y terminada mientras proporciona color consistente durante toda la temporada de crecimiento.'
                    }
                  </p>
                </Card>

                <Card className="p-6 text-center hover:shadow-glow transition-all duration-300">
                  <div className="mb-4">
                    <Star className="h-12 w-12 text-primary mx-auto" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {language === 'en' ? 'Plant Health' : 'Salud de las Plantas'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'en'
                      ? 'Protects plant roots from weather extremes and reduces soil compaction from foot traffic.'
                      : 'Protege las raíces de las plantas de extremos climáticos y reduce la compactación del suelo por el tráfico peatonal.'
                    }
                  </p>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Installation & Care Guide */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Professional Installation & Care' : 'Instalación y Cuidado Profesional'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {language === 'en'
                ? 'Follow our expert guidelines for proper mulch installation and maintenance to maximize benefits and longevity.'
                : 'Siga nuestras pautas expertas para la instalación y mantenimiento adecuado del mulch para maximizar los beneficios y la longevidad.'
              }
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Installation Guide */}
            <Card className="p-8 hover:shadow-glow transition-all duration-300">
              <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                <CheckCircle className="h-6 w-6 text-primary mr-3" />
                {language === 'en' ? 'Installation Best Practices' : 'Mejores Prácticas de Instalación'}
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">
                      {language === 'en' ? 'Site Preparation' : 'Preparación del Sitio'}
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      {language === 'en'
                        ? 'Remove existing weeds and debris. Level the area and create defined edges for a professional appearance.'
                        : 'Remover malezas y escombros existentes. Nivelar el área y crear bordes definidos para una apariencia profesional.'
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">
                      {language === 'en' ? 'Proper Depth' : 'Profundidad Adecuada'}
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      {language === 'en'
                        ? 'Apply 2-4 inches thick. Avoid piling against tree trunks or plant stems to prevent moisture-related issues.'
                        : 'Aplicar de 2-4 pulgadas de grosor. Evitar apilar contra troncos de árboles o tallos de plantas para prevenir problemas relacionados con la humedad.'
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">
                      {language === 'en' ? 'Even Distribution' : 'Distribución Uniforme'}
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      {language === 'en'
                        ? 'Spread evenly using a rake. Maintain consistent thickness across the entire area for optimal results.'
                        : 'Distribuir uniformemente usando un rastrillo. Mantener grosor consistente en toda el área para resultados óptimos.'
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">
                      {language === 'en' ? 'Initial Watering' : 'Riego Inicial'}
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      {language === 'en'
                        ? 'Lightly water the mulch after installation to help it settle and begin moisture retention benefits.'
                        : 'Regar ligeramente el mulch después de la instalación para ayudarlo a asentarse y comenzar los beneficios de retención de humedad.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Maintenance Guide */}
            <Card className="p-8 hover:shadow-glow transition-all duration-300">
              <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                <Star className="h-6 w-6 text-primary mr-3" />
                {language === 'en' ? 'Maintenance & Care Tips' : 'Consejos de Mantenimiento y Cuidado'}
              </h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-foreground mb-3 flex items-center">
                    <Droplets className="h-4 w-4 text-primary mr-2" />
                    {language === 'en' ? 'Seasonal Refresh' : 'Renovación Estacional'}
                  </h4>
                  <p className="text-muted-foreground text-sm mb-2">
                    {language === 'en'
                      ? 'Add fresh mulch annually or bi-annually to maintain depth and appearance.'
                      : 'Agregar mulch fresco anualmente o bianualmente para mantener profundidad y apariencia.'
                    }
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 ml-4">
                    <li>• {language === 'en' ? 'Spring: Add 1-2 inches of fresh mulch' : 'Primavera: Agregar 1-2 pulgadas de mulch fresco'}</li>
                    <li>• {language === 'en' ? 'Fall: Optional second application' : 'Otoño: Aplicación opcional segunda'}</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-foreground mb-3 flex items-center">
                    <Shield className="h-4 w-4 text-primary mr-2" />
                    {language === 'en' ? 'Weed Management' : 'Manejo de Malezas'}
                  </h4>
                  <p className="text-muted-foreground text-sm mb-2">
                    {language === 'en'
                      ? 'While mulch suppresses most weeds, occasional maintenance may be needed.'
                      : 'Aunque el mulch suprime la mayoría de las malezas, puede ser necesario mantenimiento ocasional.'
                    }
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 ml-4">
                    <li>• {language === 'en' ? 'Hand-pull emerging weeds promptly' : 'Quitar manualmente malezas emergentes rápidamente'}</li>
                    <li>• {language === 'en' ? 'Maintain proper mulch depth' : 'Mantener profundidad adecuada del mulch'}</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-foreground mb-3 flex items-center">
                    <Thermometer className="h-4 w-4 text-primary mr-2" />
                    {language === 'en' ? 'Weather Considerations' : 'Consideraciones Climáticas'}
                  </h4>
                  <p className="text-muted-foreground text-sm mb-2">
                    {language === 'en'
                      ? 'Texas weather can affect mulch differently throughout the year.'
                      : 'El clima de Texas puede afectar el mulch de manera diferente durante todo el año.'
                    }
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 ml-4">
                    <li>• {language === 'en' ? 'Summer: Monitor for moisture loss' : 'Verano: Monitorear pérdida de humedad'}</li>
                    <li>• {language === 'en' ? 'Winter: Protect plant roots from freezing' : 'Invierno: Proteger raíces de plantas del congelamiento'}</li>
                  </ul>
                </div>

                <div className="bg-primary/10 p-4 rounded-lg">
                  <h4 className="font-semibold text-primary mb-2">
                    {language === 'en' ? 'Pro Tip' : 'Consejo Profesional'}
                  </h4>
                  <p className="text-sm text-foreground">
                    {language === 'en'
                      ? 'Never apply mulch when soil is completely dry. Water the area lightly before mulching for best results.'
                      : 'Nunca aplicar mulch cuando el suelo esté completamente seco. Regar el área ligeramente antes del mulching para mejores resultados.'
                    }
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
      <ExpandedFAQSection faqs={mulchFaqs} language={language as 'en' | 'es'} />
      <RelatedMaterialsLinks language={language as 'en' | 'es'} exclude={['mulch']} />
      </main>

      <Footer />
    </div>
  );
};

export default MulchPage;